import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { ledgerTypes } from "../../lib/constants";
import { formatAmount, formatLedgerTypeLabel } from "../../lib/format-utils";
import type {
  Account,
  AccountJournalEntriesResponse,
  JournalEntriesResponse,
  JournalEntry,
} from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";
import { JournalEntryAuditModal } from "./journal-entry-audit-modal";
import { JournalEntryEditorModal } from "./journal-entry-editor-modal";

export const JournalEntriesView = ({
  houseId,
  search,
  setSearch,
  houseRole,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
  houseRole: "readonly" | "full";
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const yearParam = Number.parseInt(searchParams.get("year") ?? "", 10);
  const typeParam = searchParams.get("type");
  const accountParam = Number.parseInt(searchParams.get("account") ?? "", 10);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeJournalEntry, setActiveJournalEntry] = useState<JournalEntry | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [auditJournalEntryId, setAuditJournalEntryId] = useState<number | null>(null);

  const hasTypeParam = typeParam !== null;
  const activeLedgerType = hasTypeParam
    ? ledgerTypes.some((type) => type.id === typeParam)
      ? typeParam
      : ""
    : "actual";
  const activeYear = Number.isNaN(yearParam)
    ? availableYears[0] ?? new Date().getUTCFullYear()
    : yearParam;
  const selectedAccountId = Number.isNaN(accountParam) ? null : accountParam;
  const isReadonly = houseRole === "readonly";

  const loadAccounts = async () => {
    if (!houseId) {
      setAccounts([]);
      return;
    }
    const response = await fetchJson<{ accounts: Account[] }>(`/api/houses/${houseId}/accounts`);
    setAccounts(response.accounts);
  };

  const loadJournalEntries = async () => {
    if (!houseId) {
      setJournalEntries([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (selectedAccountId) {
        const response = await fetchJson<AccountJournalEntriesResponse>(
          `/api/houses/${houseId}/accounts/${selectedAccountId}/journal-entries?year=${activeYear}`,
        );
        setJournalEntries(response.journalEntries);
        setAvailableYears(Array.isArray(response.years) ? response.years : []);
        if (Number.isNaN(yearParam) || response.year !== yearParam) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
        return;
      }
      const query = new URLSearchParams();
      query.set("year", String(activeYear));
      if (activeLedgerType) {
        query.set("type", activeLedgerType);
      }
      const response = await fetchJson<JournalEntriesResponse>(
        `/api/houses/${houseId}/journal-entries?${query.toString()}`,
      );
      setJournalEntries(response.journalEntries);
      setAvailableYears(Array.isArray(response.years) ? response.years : []);
      if (Number.isNaN(yearParam) || response.year !== yearParam) {
        const nextParams = new URLSearchParams(search);
        nextParams.set("year", String(response.year));
        setSearch(`?${nextParams.toString()}`, { replace: true });
      }
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : t("entries.loadFailed", "Failed to load journal entries"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAccounts();
  }, [houseId]);

  useEffect(() => {
    void loadJournalEntries();
  }, [houseId, activeLedgerType, activeYear, search, selectedAccountId, yearParam, setSearch]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("entries.title", "Journal entries")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("entries.selectHouse", "Select a house to manage journal entries.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const accountsById = new Map(accounts.map((account) => [account.id, account]));
  const selectedAccount = selectedAccountId ? accountsById.get(selectedAccountId) ?? null : null;
  const visibleJournalEntries = useMemo(
    () =>
      activeLedgerType
        ? journalEntries.filter((entry) => entry.ledgerType === activeLedgerType)
        : journalEntries,
    [activeLedgerType, journalEntries],
  );
  const updateFilter = (next: { year?: string; type?: string; account?: string }) => {
    const nextParams = new URLSearchParams(search);
    if (next.year !== undefined) {
      nextParams.set("year", next.year);
    }
    if (next.type !== undefined) {
      nextParams.set("type", next.type);
    }
    if (next.account !== undefined) {
      if (next.account) {
        nextParams.set("account", next.account);
      } else {
        nextParams.delete("account");
      }
    }
    setSearch(`?${nextParams.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">{t("entries.title", "Journal entries")}</p>
          <p className="text-sm text-muted">
            {selectedAccount
              ? t("entries.forAccount", "Journal entries for {name}.", {
                  name: selectedAccount.labelKey
                    ? t(selectedAccount.labelKey, selectedAccount.name)
                    : selectedAccount.name,
                })
              : t("entries.subtitle", "Create, edit, and review journal entries.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-ink" htmlFor="entries-year">
              {t("common.year", "Year")}
            </label>
            <select
              id="entries-year"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={String(activeYear)}
              onChange={(event) => updateFilter({ year: event.target.value })}
              disabled={!availableYears.length}
            >
              {(availableYears.length ? availableYears : [activeYear]).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-ink" htmlFor="entries-type">
              {t("entries.type", "Type")}
            </label>
            <select
              id="entries-type"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={activeLedgerType}
              onChange={(event) => updateFilter({ type: event.target.value })}
            >
              <option value="">{t("entries.all", "All")}</option>
              {ledgerTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {t(type.labelKey, type.label)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-ink" htmlFor="entries-account">
              {t("entries.account", "Account")}
            </label>
            <select
              id="entries-account"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={selectedAccountId ? String(selectedAccountId) : ""}
              onChange={(event) => updateFilter({ account: event.target.value })}
            >
              <option value="">{t("entries.all", "All")}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.labelKey ? t(account.labelKey, account.name) : account.name}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={() => setShowEditor(true)} disabled={isReadonly}>
            {t("entries.new", "New journal entry")}
          </Button>
        </div>
      </div>
      {isReadonly ? (
        <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t(
            "entries.readonly",
            "This house is read-only. Journal entry changes are disabled.",
          )}
        </p>
      ) : null}
      {isLoading ? (
        <p className="text-sm text-muted">{t("entries.loading", "Loading journal entries...")}</p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <Card>
        <CardHeader>
          <CardTitle>{t("entries.title", "Journal entries")}</CardTitle>
        </CardHeader>
        <CardContent>
          {visibleJournalEntries.length === 0 ? (
            <p className="text-sm text-muted">
              {t("entries.empty", "No journal entries for the selected filters.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("entries.journalDate", "Journal date")}</TableHead>
                  <TableHead>{t("entries.ledgerType", "Ledger type")}</TableHead>
                  <TableHead>{t("entries.account", "Account")}</TableHead>
                  <TableHead>{t("bankTransactions.channel", "Channel")}</TableHead>
                  <TableHead>{t("entries.notes", "Notes")}</TableHead>
                  <TableHead className="text-right">{t("bankTransactions.amount", "Amount")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleJournalEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.journalDate ?? "—"}</TableCell>
                    <TableCell>{formatLedgerTypeLabel(entry.ledgerType)}</TableCell>
                    <TableCell>
                      {entry.accountId
                        ? (() => {
                            const account = accountsById.get(entry.accountId);
                            if (!account) {
                              return t("entries.unknown", "Unknown");
                            }
                            return account.labelKey ? t(account.labelKey, account.name) : account.name;
                          })()
                        : t("entries.noAccount", "No account")}
                    </TableCell>
                    <TableCell>{entry.channel ?? "—"}</TableCell>
                    <TableCell>{entry.notes ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      {formatAmount(entry.amount, entry.currency)}
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setActiveJournalEntry(entry);
                          setShowEditor(true);
                        }}
                        disabled={isReadonly}
                      >
                        {t("common.edit", "Edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setAuditJournalEntryId(entry.id)}
                      >
                        {t("entries.audit", "Audit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {showEditor && !isReadonly ? (
        <JournalEntryEditorModal
          houseId={houseId}
          accounts={accounts}
          journalEntry={activeJournalEntry}
          onClose={() => {
            setShowEditor(false);
            setActiveJournalEntry(null);
          }}
          onSaved={loadJournalEntries}
        />
      ) : null}
      {auditJournalEntryId ? (
        <JournalEntryAuditModal
          houseId={houseId}
          journalEntryId={auditJournalEntryId}
          onClose={() => setAuditJournalEntryId(null)}
        />
      ) : null}
    </div>
  );
};
